import { useState, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ediphorDiagnosticsService } from '@/services/api/ediphorDiagnosticsService';
import { JsonImpedanceInput } from '../pipeline/JsonImpedanceInput';
import { ConfigurationSection } from '../pipeline/ConfigurationSection';
import { ExecuteResultsSection } from '../pipeline/ExecuteResultsSection';
import {
  extractImpedanceData,
  extractDeviceConfig,
  parseAndValidateJson,
} from '../../utils/jsonExtractors';

export function JsonPipelineTab() {
  const [baselineJson, setBaselineJson] = useState('');
  const [measurementJson, setMeasurementJson] = useState('');

  // Parse JSON and extract data
  const baselineData = useMemo(() => {
    const { data, error } = parseAndValidateJson(baselineJson);
    if (error || !data) return { json: null, impedance: null, config: {} };
    const impedance = extractImpedanceData(data);
    const config = extractDeviceConfig(data);
    return { json: data, impedance, config };
  }, [baselineJson]);

  const measurementData = useMemo(() => {
    const { data, error } = parseAndValidateJson(measurementJson);
    if (error || !data) return { json: null, impedance: null, config: {} };
    const impedance = extractImpedanceData(data);
    const config = extractDeviceConfig(data);
    return { json: data, impedance, config };
  }, [measurementJson]);

  // Configuration state - pre-populated from JSON
  const [pathogen, setPathogen] = useState('FLU_A');
  const [deviceId, setDeviceId] = useState(() => baselineData.config.deviceId || 'JSON_DEVICE_TEST');
  const [electrodeType, setElectrodeType] = useState(
    () => baselineData.config.electrodeType || 'WP20_4CH'
  );

  // Update device ID and electrode type when baseline data changes
  const handleUpdateFromBaseline = () => {
    if (baselineData.config.deviceId) {
      setDeviceId(baselineData.config.deviceId);
    }
    if (baselineData.config.electrodeType) {
      setElectrodeType(baselineData.config.electrodeType);
    }
  };

  // Results state
  const [result, setResult] = useState<any>(null);

  // Pipeline mutation
  const pipelineMutation = useMutation({
    mutationFn: async () => {
      if (!baselineData.impedance || !measurementData.impedance) {
        throw new Error('Both baseline and measurement JSON data are required');
      }

      return ediphorDiagnosticsService.runPipeline({
        baseline: baselineData.impedance,
        measurement: measurementData.impedance,
        pathogen,
        deviceId: deviceId || undefined,
        electrodeType,
      });
    },
    onSuccess: (data) => {
      setResult({
        requestId: data.requestId,
        submittedAt: data.submittedAt,
        status: 'PENDING',
      });
    },
  });

  const canExecute =
    !!baselineData.impedance &&
    !!measurementData.impedance &&
    !pipelineMutation.isPending;

  return (
    <div className="space-y-6">
      {/* JSON Input Section */}
      <JsonImpedanceInput
        baselineJson={baselineJson}
        onBaselineChange={setBaselineJson}
        measurementJson={measurementJson}
        onMeasurementChange={setMeasurementJson}
      />

      {/* Update from JSON Button */}
      {baselineData.config.deviceId && (
        <div className="flex justify-center">
          <button
            onClick={handleUpdateFromBaseline}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            🔄 Update Config from Baseline JSON
          </button>
        </div>
      )}

      {/* Configuration Section */}
      <ConfigurationSection
        pathogen={pathogen}
        onPathogenChange={setPathogen}
        deviceId={deviceId}
        onDeviceIdChange={setDeviceId}
        electrodeType={electrodeType}
        onElectrodeTypeChange={setElectrodeType}
      />

      {/* Results Section with Execute Button - Always Visible */}
      <ExecuteResultsSection
        isLoading={pipelineMutation.isPending}
        onExecute={() => {
          if (canExecute) {
            pipelineMutation.mutate();
          }
        }}
        result={result}
      />

      {/* Error Display */}
      {pipelineMutation.isError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-900 dark:text-red-200">
            <strong>Error:</strong> {(pipelineMutation.error as any)?.message || 'Failed to execute pipeline'}
          </p>
        </div>
      )}
    </div>
  );
}
